import { FC, useEffect, useMemo } from "react";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// components
import { Box, IconButton, TableCell } from "@mui/material";
import TablePaper from "../../components/Table/Paper";
import Table from "../../components/Table";
import TablePagination from "../../components/Table/Pagination";
import IconSvg from "../../components/IconSvg";

// hooks
import useTableData from "../../hooks/useTableData";

//utils
// import { unixTimestamp } from "../../utils/dayjs";

// types
import { Table as TableType } from '../../types/components/Table';
import { useNavigate } from "react-router-dom";

dayjs.extend( utc );

export interface LogsTableProps {
  loading: boolean;
  data: DataModel.DataLog[];
}

const LogsTable: FC<LogsTableProps> = ({ loading, data }) => {
  const navigate = useNavigate();
  const entries = useMemo(() => data.map( ( entry, index ) => {
    const patrolObj = entry.n as any;  
    return {
      no: index + 1,
      uid: entry.uid,
      time: dayjs.unix( patrolObj.patrolStartDateTime ).local().format( "D/M/YYYY hh:mm A" ),
      patrolStartDateTime: patrolObj.patrolStartDateTime,
      site: patrolObj.site?.name || "",
      route: patrolObj.route?.name || "",
      operator: patrolObj.operator?.name || "",
      checkpointsDoneCount: patrolObj.checkpoints
        .filter( ( cp: any ) => cp.checklist.every( ( item: any ) => item.checked ) )
        .length,
      incidentCount: patrolObj.checkpoints.flatMap( ( cp: any ) => cp.incidents )
        .length,
    };
  } ), [ data ]);

  const columns = useMemo<TableType.Column<any>[]>(() => {
    return [
      {
        id: "no",
        sortable: true,
        header: "NO",
        accessor: "no",
        width: 80,
      },
      {
        id: "time",
        sortable: true,
        header: "DATE & TIME",
        accessor: "time",
        sortAccessor: (row) => row.patrolStartDateTime,
        width: "15%",
      },
      {
        id: "site",
        sortable: true,
        header: "SITE",
        accessor: "site",
        width: "15%",
      },
      {
        id: "route",
        sortable: true,
        header: "ROUTE",
        accessor: "route",
        width: "15%",
      },
      {
        id: "operator",
        sortable: true,
        header: "OPERATOR",
        accessor: "operator",
        width: "15%",
      },
      {
        id: "doneCheckpointCount",
        sortable: true,
        header: "CHECKPOINTS DONE",
        accessor: "checkpointsDoneCount",
        width: "20%",
        tableCellProps: { align: "right" },
      },
      {
        id: "incidentCount",
        sortable: true,
        header: "INCIDENTS REPORTED",
        accessor: "incidentCount",
        width: "20%",
        tableCellProps: { align: "right" },
      },
      {
        id: "action",
        header: "ACTION",
        renderBodyCell: (row: any, cellProps: any) => (
          <TableCell {...cellProps} sx={{ py: 0 }}>
            <IconButton onClick={() => navigate(row.uid)}>
              <IconSvg icon="info" />
            </IconButton>
          </TableCell>
        ),
        width: 150,
        tableCellProps: { align: "right" },
      },
    ];
  }, [navigate]);

  const { displayData, order, orderBy, setData, handleSort, paginationProps } =
    useTableData<any>({
      sort: { order: "desc", orderBy: "time" },
      columns,
    });

  useEffect(() => {
    setData( entries );
  }, [ entries, setData ]);

  return (
    <TablePaper>
      <Box flexGrow={1}>
        <Table
          loading={loading}
          dataKey="uid"
          order={order}
          orderBy={orderBy as string}
          columns={columns}
          data={displayData}
          onSort={handleSort}
        />
      </Box>

      <TablePagination {...paginationProps} />
    </TablePaper>
  );
};

export default LogsTable;
